
import {track} from '../../utils/tracker';
import snakeCase = require('lodash/snakeCase');
import debounce = require('lodash/debounce');
import * as ReconnectingWebsocket from 'reconnecting-websocket';
import {
  WS_END,
  WS_PONG,
  WS_STDOUT,
  WS_STDERR,
  WS_CONNECTED_ELSEWHERE,
  WS_SEND_PING,
} from '../../redux/modules/web-socket/constants';

export interface IWebSocketMessage {
  command: string;
  data: any;
}

const PING_TIMEOUT = 20 * 1000;
const RESPONSE_TIMEOUT = 5 * 1000;
const MAX_STDOUT_MESSAGES = 500;
const REASON_UNAUTHORIZED = 'Unauthorized';

export class WebSocketClient {
  protected client: any;

  private stdout: Array<string> = [];
  private debounceStdout: any = debounce(() => {
    this.runCommand(WS_STDOUT, this.stdout);
    this.stdout = [];
  }, 10);

  constructor(
    url: string,
    protected connect: () => void,
    protected runCommand: (type: string, payload: any) => void,
    protected disconnect: () => void,
  ) {
    this.initClient(url);

    window.addEventListener('offline', () => {
      // force close ws connection and do not try to reconnect
      this.close();
      track('ws client offline');
    });
    window.addEventListener('online', () => {
      // re-init ws client
      this.initClient(url);
      track('ws client online');
    });
  }

  public send(command: string, data: any = {}): void {
    if (this.client) {
      this.client.send(JSON.stringify({command, data}));
    }
  }

  public close(): void {
    this.client.close(1000, '', {keepClosed: true});
    this.client.removeEventListener('open', this.onOpen);
    this.client.removeEventListener('message', this.onMessage);
    this.client.removeEventListener('close', this.onClose);
    this.client = null;
    track('ws client close');
  }

  protected initClient(url: string): void {
    this.client = new ReconnectingWebsocket(url);
    this.client.addEventListener('open', this.onOpen);
    this.client.addEventListener('message', this.onMessage);
    this.client.addEventListener('close', this.onClose);
    track('ws client init');
  }

  protected onMessage = (event: MessageEvent) => {
    const message: IWebSocketMessage = JSON.parse(event.data);
    const command: string = 'WS_' + snakeCase(message.command).toUpperCase();

    this.checkNoActivity();

    if (command === WS_STDOUT) {
      if (!message.data) {return;}

      message.data
        .split('\n')
        .filter(t => t.trim() !== '')
        .forEach(chunk => this.stdout.push(chunk));

      this.debounceStdout();

      if (this.stdout.length > MAX_STDOUT_MESSAGES) {
        this.debounceStdout.flush();
      }
      return; // exit
    } else if (command === WS_STDERR) {
      if (!message.data) {return;}

      message.data = message.data
        .split('\n')
        .filter(t => t.trim() !== '');
    } else if (command === WS_PONG) {
      return; // exit
    } else if (command === WS_CONNECTED_ELSEWHERE || command === WS_END) {
      this.close();
    }

    this.debounceStdout.flush();
    this.runCommand(command, message.data);
  };

  protected onOpen = () => {
    this.connect();
    this.checkNoActivity();
    track('ws client onOpen');
  };

  protected onClose = (event: CloseEvent) => {
    this.debounceStdout.flush();
    this.disconnect();

    this.responseTimeout.cancel();
    this.ping.cancel();
    track('ws client onClose');

    // reload page to make auth policy check user logged status again
    if (event.reason === REASON_UNAUTHORIZED) {
      window.location.reload();
    }
  };

  private checkNoActivity(): void {
    this.responseTimeout.cancel();
    this.ping();
  }

  /* tslint:disable:member-ordering */
  private ping = debounce(() => {
    this.send(WS_SEND_PING);
    this.responseTimeout();
  }, PING_TIMEOUT);

  private responseTimeout = debounce(() => {
    if (this.client && this.client.readyState === WebSocket.OPEN) {
      this.client.close();
    }
  }, RESPONSE_TIMEOUT);
  /* tslint:enable:member-ordering */
}
